import { Box, Divider, Grid, Group, Stack, Table, Text, ThemeIcon, Title } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { ArrowRightIcon, LockIcon, PhoneIcon } from "@phosphor-icons/react"
import dayjs from "dayjs"
import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { DisabledReason, HelpButtonGroup, HelpIcon, nextStepToast, useHelpContext } from "@dehub/theme"
import { IAuth } from "../../store/Auth"
import { message } from "../../utils/message"
import { renderBusinessName } from "../../utils/render-business-name"
import { renderFullName } from "../../utils/render-name"
import { DeliveryActionButton } from "../delivery/action-controls"
import { Dot, Tone } from "../delivery/atoms"
import { DeliveryActionState, useDeliveryActions } from "../delivery/delivery-actions"
import { translateBackendError } from "../delivery/translate-error"
import { PhotoArchivePanel } from "../supplier-delivery-distributing/photo-archive-panel"
import { DeniedCard, ListState } from "../ui"
import { CustodyStatusBadge, TransferTypeBadge, transferRoles } from "./atoms"
import classes from "./custody.module.css"
import { useCustodyMutations, useCustodyTransferQuery } from "./hooks"
import { ReceiverVerifyDialog } from "./ReceiverVerifyDialog"
import { SenderVerifyDialog } from "./SenderVerifyDialog"

interface Props {
  transferId: string
}

interface UserSummary {
  firstName: string | null
  lastName: string | null
  phone: string | null
}

/** Name, else phone, else «-» — never the user's UUID. */
const userName = (u: UserSummary | null | undefined): string => {
  if (u) {
    const name = u.lastName || u.firstName ? renderFullName(u) : ""
    if (name) return name
    if (u.phone) return u.phone
  }
  return "-"
}

const formatDateTime = (iso: string | null | undefined): string => (iso ? dayjs(iso).format("YYYY-MM-DD HH:mm") : "-")

function Party({ role, name, phone }: Readonly<{ role: string; name: string; phone: string | null }>) {
  return (
    <Box flex={1} miw={180}>
      <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)">
        {role}
      </Text>
      <Text fw={650} fz="var(--nw-fz-lead)">
        {name}
      </Text>
      {phone && (
        <Group gap={6} wrap="nowrap">
          <PhoneIcon size={14} color="var(--nw-text-2)" />
          <Text ff="monospace" fz="var(--nw-fz-mono)" c="var(--nw-text-2)">
            {phone}
          </Text>
        </Group>
      )}
    </Box>
  )
}

/** Хүлээлцэх дэлгэрэнгүй (canvas Ord_HandoverDetail). */
export function CustodyTransferDetail({ transferId }: Props) {
  const { t } = useTranslation("delivery")
  const { data: transfer, isLoading, error, mutate } = useCustodyTransferQuery(transferId)
  const { initiate, submitting } = useCustodyMutations()
  const { stateOf } = useDeliveryActions()
  const [senderOpen, { open: openSender, close: closeSender }] = useDisclosure(false)
  const [receiverOpen, { open: openReceiver, close: closeReceiver }] = useDisclosure(false)
  const myBusinessRef = useSelector((s: { auth: IAuth }) => s.auth.user?.currentBusiness?.refCode ?? null)

  // @dehub/help: the handover status drives the drawer's «Одоо» step and the status tab.
  const { set: setHelpContext } = useHelpContext()
  const transferStatus = transfer?.status
  useEffect(() => {
    setHelpContext({ status: transferStatus ?? undefined })
  }, [transferStatus, setHelpContext])

  if (isLoading) return <ListState state="loading" columns={3} />

  if (error) {
    // O208: transfer-get.ts answers 403 without ORD_DN_MNG; show the calm permission card, not the raw key.
    const status = (error as { statusCode?: number })?.statusCode
    if (status === 403) return <DeniedCard permission="ORD_DN_MNG" />
    return <ListState state="error" description={translateBackendError(error)} onRetry={() => mutate()} errorCode={status} />
  }

  if (!transfer) return null

  const handleInitiate = async () => {
    try {
      await initiate(transferId)
      nextStepToast("order.handover-detail.toast.initiate", { fallback: { title: t("custody.detail.initiateSuccess") } })
    } catch (e) {
      message.error(translateBackendError(e))
    }
  }

  const expected = transfer.expectedItems ?? []
  const received = transfer.receivedItems ?? []
  const hasReceived = received.length > 0
  const discrepancyKeys = new Set((transfer.discrepancies ?? []).map((d) => d.key))
  const itemKeys = Array.from(
    new Set([...expected.map((i) => i.sku ?? i.productId ?? ""), ...received.map((i) => i.sku ?? i.productId ?? "")]),
  ).filter(Boolean)
  const roles = transferRoles(transfer.transferType, t)

  // O212: the BE resolves «→ receiver» from order.receiverBusiness, which on a PURCHASE order is this
  // supplier itself. Never print our own business as the delivery's receiver.
  const receiverBusiness = transfer.deliveryNote?.receiverBusiness
  const showReceiver = !!receiverBusiness && (!myBusinessRef || receiverBusiness.refCode !== myBusinessRef)

  type HandoverActionKey = "initiate" | "sender-verify" | "receiver-verify"
  const action: { key: HandoverActionKey; label: string; state: DeliveryActionState; onClick: () => void } | null =
    transfer.status === "DRAFT"
      ? { key: "initiate", label: t("custody.detail.start"), state: stateOf("handoverInitiate", transfer), onClick: handleInitiate }
      : transfer.status === "INITIATED"
        ? { key: "sender-verify", label: t("custody.detail.senderVerifyCta"), state: stateOf("senderVerify", transfer), onClick: openSender }
        : transfer.status === "SENDER_VERIFIED"
          ? { key: "receiver-verify", label: t("custody.detail.receiverVerifyCta"), state: stateOf("receiverVerify", transfer), onClick: openReceiver }
          : null
  // @dehub/help reason code: permission reasons mention «эрх»; otherwise the caller is not the party that must verify.
  const actionReason = (a: { key: HandoverActionKey; state: DeliveryActionState }) =>
    a.state.allowed ? null : /эрх|permission/i.test(a.state.reason ?? "") ? "PERM" : a.key === "sender-verify" ? "NOT_SENDER" : a.key === "receiver-verify" ? "NOT_RECEIVER" : "NOT_IN_STATUS"

  type Step = { key: string; label: string; at: string | null | undefined; tone: Tone; note?: string | null }
  const steps: Step[] = [
    { key: "created", label: t("custody.detail.timelineCreated"), at: transfer.createdAt, tone: "grey" },
    { key: "initiated", label: t("custody.detail.timelineInitiated"), at: transfer.initiatedAt, tone: "amber" },
    { key: "sender", label: t("custody.status.SENDER_VERIFIED"), at: transfer.senderVerifiedAt, tone: "amber" },
    { key: "receiver", label: t("custody.status.RECEIVER_VERIFIED"), at: transfer.receiverVerifiedAt, tone: "blue" },
    { key: "completed", label: t("custody.status.COMPLETED"), at: transfer.completedAt, tone: "green" },
    {
      key: "cancelled",
      label: t("custody.status.CANCELLED"),
      at: transfer.cancelledAt,
      tone: "grey",
      note:
        transfer.cancelledReasonCode === "DUPLICATE_SUPERSEDED"
          ? t("manage.handover.duplicateCancelled")
          : transfer.cancelledReason,
    },
  ]
  const timeline = steps.filter((s) => s.key === "created" || !!s.at)

  return (
    <>
      <Grid gap="md">
        <Grid.Col span={{ base: 12, md: 8 }}>
          <Stack gap="md">
            <Box className={classes.card}>
              <Stack gap="md">
                <Group justify="space-between" align="flex-start" wrap="wrap">
                  <Group gap="xs" wrap="wrap">
                    <TransferTypeBadge type={transfer.transferType} />
                    <CustodyStatusBadge status={transfer.status} />
                  </Group>
                  {action && (
                    <Stack gap={4} align="flex-end">
                      {/* @dehub/help: attached «?» (R2) + reason popover while unavailable (R4). */}
                      <DisabledReason helpKey="order.handover-detail.reason.action" reason={actionReason(action)} when={!action.state.allowed} position="bottom-end">
                        <HelpButtonGroup helpKey={`order.handover-detail.action.${action.key}`}>
                          <DeliveryActionButton
                            data-help-id={`handover-${action.key}`}
                            state={action.state}
                            variant={action.state.allowed ? "filled" : "light"}
                            color={action.state.allowed ? "dark" : "gray"}
                            loading={submitting}
                            onClick={action.onClick}
                          >
                            {action.label}
                          </DeliveryActionButton>
                        </HelpButtonGroup>
                      </DisabledReason>
                      {/* O210: the reason is visible text, not only a tooltip on a disabled button. */}
                      {!action.state.allowed && (
                        <Group gap={6} wrap="nowrap">
                          <LockIcon size={14} color="var(--nw-text-2)" />
                          <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)">
                            {action.state.reason}
                          </Text>
                        </Group>
                      )}
                    </Stack>
                  )}
                </Group>

                {transfer.deliveryNote && (
                  <Box>
                    <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)">
                      {t("custody.detail.order")}
                    </Text>
                    <Group gap="xs" wrap="wrap" align="baseline">
                      <Text ff="monospace" fw={650} fz="var(--nw-fz-lead)">
                        {transfer.deliveryNote.refCode ?? "-"}
                      </Text>
                      {showReceiver && (
                        <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)">
                          → {renderBusinessName(receiverBusiness, "-")}
                          {receiverBusiness?.refCode && (
                            <Text span ff="monospace" ml={6}>
                              ({receiverBusiness.refCode})
                            </Text>
                          )}
                        </Text>
                      )}
                    </Group>
                  </Box>
                )}

                <Divider color="var(--nw-table-row-border)" />

                <Group justify="space-between" wrap="wrap" gap="lg" align="center">
                  <Party role={roles.from} name={userName(transfer.fromUser)} phone={transfer.fromUser?.phone ?? null} />
                  <ThemeIcon variant="light" color="gray" size={32} radius="xl">
                    <ArrowRightIcon size={16} />
                  </ThemeIcon>
                  <Party role={roles.to} name={userName(transfer.toUser)} phone={transfer.toUser?.phone ?? null} />
                </Group>
              </Stack>
            </Box>

            <Box className={classes.tableCard}>
              <Group gap="xs" px={20} py="md">
                <Title order={4} fz="var(--nw-fz-card)">
                  {t("custody.detail.goods")}
                  <HelpIcon helpKey="order.handover-detail.section.goods" variant="section" />
                </Title>
                {expected.length > 0 && (
                  <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)">
                    {t("custody.detail.itemCount", { count: expected.length })}
                  </Text>
                )}
                {(transfer.discrepancies ?? []).length > 0 && (
                  <Group gap={6} ml="auto">
                    <Dot tone="red" />
                    <Text fz="var(--nw-fz-body)">
                      {t("custody.detail.discrepancyAlert", { count: transfer.discrepancies!.length })}
                    </Text>
                  </Group>
                )}
              </Group>
              {expected.length === 0 ? (
                <Text px={20} pb={20} fz="var(--nw-fz-body)" c="var(--nw-text-2)">
                  {t("custody.detail.noGoods")}
                </Text>
              ) : (
                <Table verticalSpacing="sm" horizontalSpacing="md">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>{t("custody.detail.goods")}</Table.Th>
                      <Table.Th ta="right">{t("custody.detail.expectedQty")}</Table.Th>
                      {hasReceived && <Table.Th ta="right">{t("custody.detail.receivedQty")}</Table.Th>}
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {itemKeys.map((key) => {
                      const e = expected.find((i) => (i.sku ?? i.productId) === key)
                      const r = received.find((i) => (i.sku ?? i.productId) === key)
                      const display = e ?? r
                      const diff = discrepancyKeys.has(key)
                      return (
                        <Table.Tr key={key}>
                          <Table.Td>
                            <Text fz="var(--nw-fz-body)">{display?.name ?? "-"}</Text>
                            {display?.sku && (
                              <Text ff="monospace" fz="var(--nw-fz-mono)" c="var(--nw-text-2)">
                                {display.sku}
                              </Text>
                            )}
                          </Table.Td>
                          <Table.Td ta="right">
                            <Text ff="monospace" fz="var(--nw-fz-mono)">
                              {e?.quantity ?? "-"} {display?.unit ?? ""}
                            </Text>
                          </Table.Td>
                          {hasReceived && (
                            <Table.Td ta="right">
                              <Group gap={6} justify="flex-end" wrap="nowrap">
                                {diff && <Dot tone="red" />}
                                <Text ff="monospace" fz="var(--nw-fz-mono)" fw={diff ? 650 : 400}>
                                  {r?.quantity ?? "-"}
                                </Text>
                              </Group>
                            </Table.Td>
                          )}
                        </Table.Tr>
                      )
                    })}
                  </Table.Tbody>
                </Table>
              )}
            </Box>
          </Stack>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 4 }}>
          <Stack gap="md">
            <Box className={classes.card}>
              <Title order={4} fz="var(--nw-fz-card)" mb="md">
                {t("custody.detail.progress")}
                <HelpIcon helpKey="order.handover-detail.section.progress" variant="section" />
              </Title>
              <Stack gap="md">
                {timeline.map((step) => (
                  <Group key={step.key} gap="sm" wrap="nowrap" align="flex-start">
                    <Box mt={6}>
                      <Dot tone={step.tone} />
                    </Box>
                    <Box>
                      <Text fw={600} fz="var(--nw-fz-body)">
                        {step.label}
                      </Text>
                      <Text ff="monospace" fz="var(--nw-fz-mono)" c="var(--nw-text-2)">
                        {formatDateTime(step.at)}
                      </Text>
                      {step.note && (
                        <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)">
                          {step.note}
                        </Text>
                      )}
                    </Box>
                  </Group>
                ))}
              </Stack>
            </Box>

            {transfer.deliveryNoteId && <PhotoArchivePanel deliveryNoteId={transfer.deliveryNoteId} hideProofMedia />}
          </Stack>
        </Grid.Col>
      </Grid>

      <SenderVerifyDialog opened={senderOpen} onClose={closeSender} transferId={transferId} transfer={transfer} />
      <ReceiverVerifyDialog opened={receiverOpen} onClose={closeReceiver} transferId={transferId} transfer={transfer} />
    </>
  )
}
